import { api, apiSchemas } from '@verifime/api-definition';
import {
  TrafficLight as TrafficLightComponent,
  TTrafficLightData,
  TTrafficLightOperations,
} from '@verifime/components';
import { useEffect, useState, useMemo } from 'react';
import { z } from 'zod';

const TRAFFIC_LIGHT_NAME_MAPPING: { [key in keyof TTrafficLightData]: string } = {
  Capture: 'Document Capture',
  DataMatch: 'Document Data Match',
  Liveness: 'Face Check: Liveness',
  PhotoMatch: 'Face Check: Photo Match',
};

type TTrafficLightResponse = z.infer<typeof apiSchemas.VerificationPipelineResponse>;
type TTrafficLightResponseValues = Array<
  z.infer<typeof apiSchemas.VerificationPipelineResponseDocument> &
    z.infer<typeof apiSchemas.VerificationPipelineResponseFaceCheck>
>;

const extractTrafficLightData = (
  trafficLightResponse: TTrafficLightResponse,
): TTrafficLightData => {
  const result = (Object.values(trafficLightResponse) as TTrafficLightResponseValues).reduce(
    (tally, values) => {
      const attachedName = Object.entries(values.pipeline).reduce((tally, [k, v]) => {
        return {
          ...tally,
          [k]: { ...v, name: TRAFFIC_LIGHT_NAME_MAPPING[k as keyof TTrafficLightData] },
        };
      }, {});
      return { ...tally, ...attachedName };
    },
    {},
  );
  return result as TTrafficLightData;
};

type TTrafficLightProps = {
  customerId: string;
  operations?: TTrafficLightOperations;
  onDataChange?: (trafficLightData: TTrafficLightData) => void;
};

export function CustomerOverallTrafficLight({
  customerId,
  operations,
  onDataChange,
}: Readonly<TTrafficLightProps>) {
  // useMemo must run on every render regardless of customerId, so it is called before the guard
  // below rather than after it. When customerId is absent there is nothing to fetch; the memo skips
  // the call in that case, and the guard below still returns before the unresolved promise is used.
  const momoizedPromise: Promise<TTrafficLightResponse> | undefined = useMemo(
    () =>
      customerId
        ? api.getV1personCustomerIdoverallverificationpipeline({
            params: { customerId },
          })
        : undefined,
    [customerId],
  );

  if (!customerId || !momoizedPromise) {
    return;
  }

  return (
    <TrafficLightInternal
      trafficApiResponsePromise={momoizedPromise}
      operations={operations}
      onDataChange={onDataChange}
    />
  );
}

function TrafficLightInternal({
  trafficApiResponsePromise,
  operations,
  onDataChange,
}: Readonly<
  {
    trafficApiResponsePromise: Promise<TTrafficLightResponse>;
  } & Pick<TTrafficLightProps, 'operations' | 'onDataChange'>
>) {
  const [trafficLightData, setTrafficLightData] = useState<TTrafficLightData | null>(null);

  useEffect(() => {
    trafficApiResponsePromise
      .then((data) => {
        const trafficLightData = extractTrafficLightData(data);
        setTrafficLightData(trafficLightData);
        onDataChange?.(trafficLightData);
      })
      .catch((err) => {
        console.warn('Failed to get customer verification pipeline:', err);
      });
  }, [trafficApiResponsePromise]);

  return <TrafficLightComponent data={trafficLightData} operations={operations} />;
}
